import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, RefreshCw, AlertCircle } from "lucide-react";
import { format } from "date-fns";

interface MediaOwnerUser {
  id: string;
  email: string;
  media_owner_name: string;
  display_name: string | null;
  is_active: boolean;
  created_at: string;
}

const MEDIA_OWNERS = ['Alan', 'Waki', 'Attention', 'TableAds', 'Rotana Signs', 'Wave Media'];

const MediaOwnerManagementPanel = () => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const { user } = useAuth();
  const [users, setUsers] = useState<MediaOwnerUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', media_owner_name: '', display_name: '' });

  const fetchUsers = async () => {
    setLoading(true);
    setFetchError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setFetchError(language === 'ar' ? 'لا توجد جلسة نشطة. يرجى تحديث الصفحة.' : 'No active session. Please refresh the page.');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('media_owner_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching media owner users:', error);
        setFetchError(error.message);
        toast({ title: language === 'ar' ? 'خطأ في جلب البيانات' : 'Error fetching users', description: error.message, variant: 'destructive' });
      } else {
        setUsers((data || []) as MediaOwnerUser[]);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setFetchError(language === 'ar' ? 'خطأ غير متوقع' : 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAdd = async () => {
    const { data: existing } = await supabase
      .from('media_owner_users')
      .select('id')
      .eq('email', newUser.email.toLowerCase().trim())
      .maybeSingle();

    if (existing) {
      toast({
        title: language === 'ar' ? 'البريد الإلكتروني مستخدم' : 'Email already exists',
        description: language === 'ar'
          ? 'هذا البريد الإلكتروني مضاف بالفعل كمستخدم مالك وسائط'
          : 'This email is already registered as a media owner user',
        variant: 'destructive',
      });
      return;
    }

    const { error } = await supabase.from('media_owner_users').insert({
      email: newUser.email.toLowerCase().trim(),
      media_owner_name: newUser.media_owner_name,
      display_name: newUser.display_name || null,
      added_by: user?.id,
    });

    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      toast({
        title: language === 'ar' ? 'تمت الإضافة' : 'Added',
        description: language === 'ar' ? 'تم إضافة مستخدم مالك الوسائط' : 'Media owner user added',
      });
      setShowAddDialog(false);
      setNewUser({ email: '', media_owner_name: '', display_name: '' });
      fetchUsers();
    }
  };

  const handleToggleActive = async (userId: string, currentActive: boolean) => {
    await supabase.from('media_owner_users').update({ is_active: !currentActive }).eq('id', userId);
    fetchUsers();
  };

  const handleDelete = async (userId: string) => {
    await supabase.from('media_owner_users').delete().eq('id', userId);
    fetchUsers();
    toast({ title: language === 'ar' ? 'تم الحذف' : 'Deleted' });
  };

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div></div>;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{language === 'ar' ? 'إدارة مالكي الوسائط' : 'Media Owner Management'}</CardTitle>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={fetchUsers}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button size="sm"><Plus className="h-4 w-4 mr-1" />{language === 'ar' ? 'إضافة مستخدم' : 'Add User'}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{language === 'ar' ? 'إضافة مستخدم مالك وسائط' : 'Add Media Owner User'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Input
                  type="email"
                  placeholder={language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                  value={newUser.email}
                  onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))}
                />
                <Select value={newUser.media_owner_name} onValueChange={v => setNewUser(p => ({ ...p, media_owner_name: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'ar' ? 'اختر مالك الوسائط' : 'Select Media Owner'} />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDIA_OWNERS.map(mo => (
                      <SelectItem key={mo} value={mo}>{mo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder={language === 'ar' ? 'اسم العرض (اختياري)' : 'Display Name (optional)'}
                  value={newUser.display_name}
                  onChange={e => setNewUser(p => ({ ...p, display_name: e.target.value }))}
                />
                <Button onClick={handleAdd} disabled={!newUser.email || !newUser.media_owner_name} className="w-full">
                  {language === 'ar' ? 'إضافة' : 'Add'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {fetchError && (
          <div className="flex items-center gap-2 text-destructive bg-destructive/10 rounded-md p-3 mb-4 text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{fetchError}</span>
          </div>
        )}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</TableHead>
              <TableHead>{language === 'ar' ? 'مالك الوسائط' : 'Media Owner'}</TableHead>
              <TableHead>{language === 'ar' ? 'الاسم' : 'Name'}</TableHead>
              <TableHead>{language === 'ar' ? 'نشط' : 'Active'}</TableHead>
              <TableHead>{language === 'ar' ? 'التاريخ' : 'Added'}</TableHead>
              <TableHead>{language === 'ar' ? 'إجراءات' : 'Actions'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.email}</TableCell>
                <TableCell>
                  <Badge variant="outline">{u.media_owner_name}</Badge>
                </TableCell>
                <TableCell>{u.display_name || '—'}</TableCell>
                <TableCell>
                  <Switch checked={u.is_active} onCheckedChange={() => handleToggleActive(u.id, u.is_active)} />
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {format(new Date(u.created_at), 'MMM d, yyyy')}
                </TableCell>
                <TableCell>
                  <Button size="icon" variant="ghost" onClick={() => handleDelete(u.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && !fetchError && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  {language === 'ar' ? 'لم يتم إضافة مستخدمين بعد' : 'No media owner users added yet'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default MediaOwnerManagementPanel;
